import DoDisturbOffIcon from '@mui/icons-material/DoDisturbOff';
import DoDisturbOnIcon from '@mui/icons-material/DoDisturbOn';
import { Box, Checkbox, Typography } from '@mui/material';
import { useTheme, type Theme } from '@mui/material/styles';
import {
  DataGrid,
  useGridApiContext,
  type GridRenderCellParams,
  type GridRenderEditCellParams,
  type GridRowId,
  type GridRowModesModel,
  type GridRowsProp
} from '@mui/x-data-grid';
import React, { useEffect, useState, type ChangeEvent } from 'react';
import ActionsCell from '../ui/ActionsCell';
import HistorialCell from '../ui/HistorialCell';
import type { AnimalCompraRow } from './AnimalCompraRow';
import { colDefinitions } from './column-definitions';

interface AnimalesCompraProps {
  rows: GridRowsProp<AnimalCompraRow>;
  rowModesModel: GridRowModesModel;
  onRowModesModelChange: (rowModesModel: GridRowModesModel) => void;
  readonly: boolean;
  onClickHistorialSiiniga: (folio: string, partida: number) => void;
  onClickHistorialTemporal: (folio: string, partida: number) => void;
  onCancel: (id: GridRowId) => void;
  onEdit: (id: GridRowId) => void;
  onSave: (id: GridRowId) => void;
  onRowUpdate: (
    newRow: AnimalCompraRow,
    oldRow: AnimalCompraRow
  ) => AnimalCompraRow | Promise<AnimalCompraRow>;
}

const getStyles = (theme: Theme) => ({
  dataGridContainer: {
    height: 280,
    maxWidth: '95vw'
  },
  dataGrid: {
    borderColor: theme.palette.primary.main,
    backgroundColor: theme.palette.background.paper,
    '& .MuiDataGrid-columnHeader, .MuiDataGrid-cell, .MuiDataGrid-iconSeparator': {
      borderRight: `1px solid ${theme.palette.primary.main}`
    },
    '& .MuiDataGrid-columnHeaderTitle': {
      fontWeight: 'bold'
    },
    '& .MuiDataGrid-columnsContainer, .MuiDataGrid-cell': {
      borderBottom: `1px solid ${theme.palette.primary.main}`
    }
  }
});

const AnimalesCompra = ({
  rows,
  rowModesModel,
  readonly,
  onRowModesModelChange,
  onClickHistorialSiiniga,
  onClickHistorialTemporal,
  onCancel,
  onEdit,
  onSave,
  onRowUpdate
}: AnimalesCompraProps) => {
  const theme = useTheme();
  const classes = getStyles(theme);
  const [columnDefs, setColumnDefs] = useState(colDefinitions);

  const renderHistorialSiinigaCell = (params: GridRenderCellParams) => {
    return <HistorialCell {...params} onClick={onClickHistorialSiiniga} />;
  };

  const renderHistorialTemporalCell = (params: GridRenderCellParams) => {
    return <HistorialCell {...params} onClick={onClickHistorialTemporal} />;
  };

  const tienePapelesRenderCell = (params: GridRenderCellParams) => {
    return <Typography>{params.value === true ? 'SÍ' : 'NO'}</Typography>;
  };
  const omiteDescuentoRenderCell = (params: GridRenderCellParams) => {
    const api = useGridApiContext();
    api.current.getCellMode(params.id, params.field);
    return <Typography>{params.value === true ? 'SÍ' : 'NO'}</Typography>;
  };
  const renderOmiteDescuentoEditCell = ({ id, value, field }: GridRenderEditCellParams) => {
    const api = useGridApiContext();
    const handleChange = (event: ChangeEvent<any>) => {
      const newValue = event.target.checked;
      api.current.setEditCellValue({ id, field, value: newValue }, event);
    };

    return (
      <Checkbox
        checked={value}
        icon={<DoDisturbOffIcon />}
        checkedIcon={<DoDisturbOnIcon />}
        onChange={handleChange}
      />
    );
  };

  const renderActionsCell = (params: GridRenderCellParams) => {
    return <ActionsCell {...params} onCancel={onCancel} onEdit={onEdit} onSave={onSave} />;
  };

  useEffect(() => {
    setColumnDefs((current) =>
      current.map((col) => {
        switch (col.field) {
          case 'historialSiiniga':
            return {
              ...col,
              renderCell: renderHistorialSiinigaCell
            };
          case 'historialTemporal':
            return {
              ...col,
              renderCell: renderHistorialTemporalCell
            };
          case 'omiteDescuento':
            return {
              ...col,
              renderCell: omiteDescuentoRenderCell,
              renderEditCell: renderOmiteDescuentoEditCell
            };
          case 'tienePapeles':
            return {
              ...col,
              renderCell: tienePapelesRenderCell
            };
          case 'actions':
            return {
              ...col,
              renderCell: renderActionsCell
            };
          default:
            return col;
        }
      })
    );
  }, []);

  const columnsVisibility = (noEditable: boolean) => ({
    omiteDescuento: !noEditable,
    actions: !noEditable
  });

  return (
    <Box sx={classes.dataGridContainer}>
      <DataGrid
        sx={classes.dataGrid}
        rows={rows}
        columns={columnDefs}
        editMode={'row'}
        isCellEditable={(params) => (readonly ? false : params.colDef.editable === true)}
        columnVisibilityModel={columnsVisibility(readonly)}
        rowModesModel={rowModesModel}
        onRowModesModelChange={onRowModesModelChange}
        processRowUpdate={onRowUpdate}
        autoHeight={false}
        disableColumnMenu={true}
        disableColumnSorting={true}
        disableRowSelectionOnClick={true}
        disableColumnResize={true}
      />
    </Box>
  );
};

export default React.memo(AnimalesCompra);
